import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
    ArrowLeft,
    Search,
    ChevronRight,
    FileSpreadsheet,
    Trash2,
    RefreshCw,
    Download,
    Mail,
    Zap,
} from 'lucide-react';
import { contractFilesApi, orderAllocationApi, supportingDataApi } from '../services/api';
import { ContractFile, ContractNote, CustodianAllocationStatus, FILE_TYPE_LABELS, ORDER_CALC_TYPE_MUTUAL_FUND, Scheme } from '../types/client';
import styles from './ContractNotes.module.css';
import {
    PageFrame, PageHeader, Surface, FilterBar, FilterSelect, FilterOption, Button, Placeholder,
    Note, Tag, Pill, StatusPill, StatusMeta, Modal, confirmColors,
} from '@/components/ui';
import ContractNotesTable from '@/components/contract_notes/ContractNotesTable';
import shared from '@/components/utils/shared.module.css';
import { SCHEME_REGION_GLOBAL } from '@/config/constants';
import { todayDateParam } from '@/utils/formatters';

// Global schemes have no contract-note file to upload — their notes are pulled from the
// live IBKR session instead.

/** How far a file's notes have been allocated. Anything without an entry reads as Pending. */
const FILE_ALLOCATION_META: Partial<Record<NonNullable<ContractFile['allocation_status']>, StatusMeta>> = {
    full: { label: 'Allocated', tone: 'positive', title: 'Fully Allocated' },
    partial: { label: 'Partial', tone: 'warning', title: 'Partially Allocated' },
};
const FILE_ALLOCATION_PENDING: StatusMeta = { label: 'Pending', tone: 'neutral', title: 'Not Allocated' };

const ContractFilesManage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dateParam = searchParams.get('date');

    // --- Contract files state ---
    const [contractFiles, setContractFiles] = useState<ContractFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedFileNotes, setSelectedFileNotes] = useState<{
        fileName: string;
        notes: ContractNote[];
    } | null>(null);

    // --- Allocation state ---
    const [custodians, setCustodians] = useState<CustodianAllocationStatus[]>([]);
    const [allocLoading, setAllocLoading] = useState(!!dateParam);
    const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
    const [allocErrors, setAllocErrors] = useState<Record<string, string[]>>({});

    // --- Global (IBKR) contract-note fetch state ---
    const [globalSchemes, setGlobalSchemes] = useState<Scheme[]>([]);
    const [selectedGlobalScheme, setSelectedGlobalScheme] = useState('');
    const [fetchingGlobal, setFetchingGlobal] = useState(false);
    const isCurrentSession = dateParam === todayDateParam();
    const globalSchemeOptions: FilterOption[] = globalSchemes
        .filter((scheme): scheme is Scheme & { code: string } => !!scheme.code)
        .map(scheme => ({ value: scheme.code, label: scheme.code }));

    // ── Fetch contract files ────────────────────────────────────────
    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        try {
            const params: Record<string, any> = { ordering: '-created_ts' };
            if (dateParam) params.contract_note_date = dateParam;
            const files = await contractFilesApi.getContractFiles(params);
            setContractFiles(files);
        } catch (error) {
            toast.error('Failed to load files');
        } finally {
            setIsLoading(false);
        }
    }, [dateParam]);

    // ── Fetch allocation status (custodians from contract notes + their alloc counts) ──
    const fetchAllocationStatus = useCallback(async () => {
        if (!dateParam) return;
        setAllocLoading(true);
        try {
            const res = await orderAllocationApi.getAllocationStatus(dateParam);
            setCustodians(res);
        } catch {
            toast.error('Failed to load allocation status');
        } finally {
            setAllocLoading(false);
        }
    }, [dateParam]);

    useEffect(() => {
        fetchFiles();
        fetchAllocationStatus();
    }, [fetchFiles, fetchAllocationStatus]);

    // ── Global schemes for the IBKR fetch control ──────────────────
    useEffect(() => {
        if (!isCurrentSession) return;
        supportingDataApi.getSchemes()
            .then(schemes => {
                const global = schemes.filter(s => s.region === SCHEME_REGION_GLOBAL && s.is_active);
                setGlobalSchemes(global);
                setSelectedGlobalScheme(prev => prev || global[0]?.code || '');
            })
            .catch(() => setGlobalSchemes([]));
    }, [isCurrentSession]);

    const handleFetchGlobalNotes = async () => {
        if (!selectedGlobalScheme) return;
        setFetchingGlobal(true);
        setAllocErrors(prev => ({ ...prev, global: [] }));
        try {
            const { result } = await orderAllocationApi.fetchGlobalContractNotes(selectedGlobalScheme);
            if (result.errors.length) {
                toast.warning(`${selectedGlobalScheme}: ${result.contract_notes} contract note(s) built, some fills skipped`);
                setAllocErrors(prev => ({ ...prev, global: result.errors }));
            } else if (result.contract_notes === 0) {
                toast.info(`${selectedGlobalScheme}: no fills in the current IBKR session`);
            } else {
                toast.success(`${selectedGlobalScheme}: ${result.contract_notes} contract note(s) built`);
            }
            await Promise.all([fetchAllocationStatus(), fetchFiles()]);
        } catch (err: any) {
            const data = err?.response?.data;
            const message = data?.message || data?.error || 'Failed to fetch global contract notes';
            toast.error(message);
            setAllocErrors(prev => ({ ...prev, global: [message] }));
        } finally {
            setFetchingGlobal(false);
        }
    };

    // ── Allocation actions ────────────────────────────────────────
    const cardKey = (custody: CustodianAllocationStatus) => `${custody.id}-${custody.order_calc_type}`;

    const handleAllocate = async (custody: CustodianAllocationStatus) => {
        if (!dateParam) return;
        const ck = cardKey(custody);
        setAllocErrors(prev => ({ ...prev, [ck]: [] }));
        setActionLoading(prev => ({ ...prev, [`allocate-${ck}`]: true }));
        try {
            const res = await orderAllocationApi.triggerAllocation(custody.id, dateParam, custody.order_calc_type);
            const partialErrors: string[] = res.errors ?? [];
            if (partialErrors?.length) {
                toast.warning(`Allocation partially done for ${custody.name}`);
                setAllocErrors(prev => ({ ...prev, [ck]: partialErrors }));
            } else {
                toast.success(`Allocation done for ${custody.name}`);
            }
            await Promise.all([fetchAllocationStatus(), fetchFiles()]);
        } catch (err: any) {
            const data = err?.response?.data;
            const errors: string[] = data?.errors?.length
                ? data.errors
                : data?.error
                    ? [data.error]
                    : [`Allocation failed for ${custody.name}`];
            toast.error(errors[0]);
            setAllocErrors(prev => ({ ...prev, [ck]: errors }));
        } finally {
            setActionLoading(prev => ({ ...prev, [`allocate-${ck}`]: false }));
        }
    };

    const handleDownload = async (custody: CustodianAllocationStatus) => {
        if (!dateParam) return;
        const ck = cardKey(custody);
        setActionLoading(prev => ({ ...prev, [`download-${ck}`]: true }));
        try {
            const blob = await orderAllocationApi.downloadAllocation(custody.id, dateParam, custody.order_calc_type);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = custody.order_calc_type === ORDER_CALC_TYPE_MUTUAL_FUND
                ? `0051_MF_allocation_${dateParam}.csv`
                : `allocation_${custody.name}_${dateParam}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
            toast.success(`Downloaded allocation files for ${custody.name}`);
        } catch {
            toast.error(`Download failed for ${custody.name}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [`download-${ck}`]: false }));
        }
    };

    const handleSendEmail = async (custody: CustodianAllocationStatus) => {
        if (!dateParam) return;
        const { value: recipientStr, isConfirmed } = await Swal.fire({
            title: `Send Allocation Email`,
            text: `Enter recipient email address(es) for ${custody.name} (comma-separated):`,
            input: 'text',
            inputPlaceholder: 'e.g. ops@example.com, mgr@example.com',
            showCancelButton: true,
            ...confirmColors('primary'),
            confirmButtonText: 'Send',
            inputValidator: (v) => (!v.trim() ? 'Please enter at least one email.' : null),
        });
        if (!isConfirmed || !recipientStr) return;

        const recipients = recipientStr.split(',').map((s: string) => s.trim()).filter(Boolean);
        const ck = cardKey(custody);
        setActionLoading(prev => ({ ...prev, [`send-${ck}`]: true }));
        try {
            await orderAllocationApi.sendAllocationEmail(custody.id, dateParam, recipients, custody.order_calc_type);
            toast.success(`Email sent for ${custody.name}`);
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? `Email failed for ${custody.name}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [`send-${ck}`]: false }));
        }
    };

    const handleDeleteAllocation = async (custody: CustodianAllocationStatus) => {
        if (!dateParam) return;
        const result = await Swal.fire({
            title: 'Delete Allocations?',
            text: `This will delete all ${custody.allocation_count} allocation record(s) for ${custody.name}.`,
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, delete!',
        });
        if (!result.isConfirmed) return;

        const ck = cardKey(custody);
        setActionLoading(prev => ({ ...prev, [`delete-alloc-${ck}`]: true }));
        try {
            await orderAllocationApi.deleteAllocation(custody.id, dateParam, custody.order_calc_type);
            toast.success(`Allocations deleted for ${custody.name}`);
            await Promise.all([fetchAllocationStatus(), fetchFiles()]);
        } catch {
            toast.error(`Delete failed for ${custody.name}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [`delete-alloc-${ck}`]: false }));
        }
    };

    // ── Contract file actions ─────────────────────────────────────
    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will also delete all associated contract notes.',
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, delete it!',
        });

        if (!result.isConfirmed) return;
        try {
            await contractFilesApi.deleteContractFile(id);
            toast.success('File deleted successfully');
            await Promise.all([fetchFiles(), fetchAllocationStatus()]);
        } catch {
            toast.error('Failed to delete file');
        }
    };

    const viewFileNotes = async (file: ContractFile) => {
        try {
            const response = await contractFilesApi.getContractFileNotes(file.id);
            setSelectedFileNotes({ fileName: response.file_name, notes: response.notes });
        } catch {
            toast.error('Failed to load notes');
        }
    };

    const filteredFiles = contractFiles.filter(file => {
        const matchesSearch = file.file.toLowerCase().includes(searchQuery.toLowerCase());
        const fileDate = file.contract_note_date || new Date(file.created_ts).toLocaleDateString('en-CA');
        const matchesDate = !dateParam || fileDate === dateParam;
        return matchesSearch && matchesDate;
    });

    const handleDeleteAll = async () => {
        const label = dateParam
            ? `for ${(() => { const [dd, mm, yyyy] = dateParam!.split('-'); return new Date(`${yyyy}-${mm}-${dd}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); })()}`
            : 'for all dates';

        const count = filteredFiles.length;
        if (count === 0) return;

        const result = await Swal.fire({
            title: `Delete ${count} Files?`,
            text: `This will delete all ${count} contract file(s) ${label} and their associated contract notes and allocations. This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, delete all!',
        });
        if (!result.isConfirmed) return;

        setIsLoading(true);
        try {
            // Bulk delete using the optimized endpoint
            // Passing the dateParam ensuring we delete for the correct business date
            const result = await contractFilesApi.deleteAllContractFiles(dateParam ? { date: dateParam } : undefined);

            if (result.deleted_count === 0) {
                toast.error('No files were deleted. ');
            } else {
                toast.success(`Deleted ${result.deleted_count} contract file(s) ${label}`);
            }
            await Promise.all([fetchFiles(), fetchAllocationStatus()]);
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Failed to delete all contract files';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const dateLabel = dateParam
        ? (() => { const [dd, mm, yyyy] = dateParam!.split('-'); return new Date(`${yyyy}-${mm}-${dd}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); })()
        : null;


    return (
        <PageFrame
            header={
                <PageHeader
                    title={`Manage Files${dateLabel ? ` — ${dateLabel}` : ''}`}
                    subtitle="Files uploaded for this trade date, and what each one produced."
                    actions={
                        <>
                            <Button onClick={() => navigate('/contract-notes-process')} icon={<ArrowLeft size={15} />}>
                                Back
                            </Button>
                            {filteredFiles.length > 0 && (
                                <Button variant="danger" onClick={handleDeleteAll} icon={<Trash2 size={15} />}>
                                    Delete All
                                </Button>
                            )}
                        </>
                    }
                />
            }
        >


            <FilterBar
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search by file name…"
            />

            {/* ── Contract files ── */}
            <Surface title="Files uploaded" meta={`${filteredFiles.length} file${filteredFiles.length === 1 ? '' : 's'}`}>
                {isLoading ? (
                    <Placeholder kind="loading" message="Loading files…" />
                ) : (
                    <div className={shared['table-scroll']}>
                            <table className={shared.table}>
                                <thead>
                                    <tr>
                                        <th>File Name</th>
                                        <th>Type</th>
                                        <th>Allocation</th>
                                        <th>Processed On</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredFiles.length > 0 ? (
                                        filteredFiles.map((file) => (
                                            <tr key={file.id}>
                                                <td>
                                                    <div className={styles['file-cell']}>
                                                        <FileSpreadsheet size={15} color="var(--accent)" />
                                                        <span>{file.file.split('/').pop()}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Tag>{FILE_TYPE_LABELS[file.type] ?? 'Derivative'}</Tag>
                                                </td>
                                                <td>
                                                    <StatusPill
                                                        value={file.allocation_status}
                                                        meta={FILE_ALLOCATION_META}
                                                        fallback={FILE_ALLOCATION_PENDING}
                                                    />
                                                </td>
                                                <td>{new Date(file.created_ts).toLocaleString('en-IN')}</td>
                                                <td>
                                                    <div className={styles['row-actions']} style={{ justifyContent: 'flex-start' }}>
                                                        <Button size="sm" onClick={() => viewFileNotes(file)}>
                                                            Details <ChevronRight size={13} />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="danger"
                                                            onClick={() => handleDelete(file.id)}
                                                            icon={<Trash2 size={14} />}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5}>
                                                <Placeholder message="No files found" />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                )}
            </Surface>

            {/* ── Order allocations for this date ── */}
            {dateParam && (
                <Surface
                    title="Order Allocations"
                    actions={
                        <>
                            {/* Global (IBKR) schemes have no file to upload: their contract
                                notes come from the live session, which only covers today. */}
                            {isCurrentSession && globalSchemes.length > 0 && (
                                <>
                                    <div style={{ minWidth: 160 }}>
                                        <FilterSelect
                                            label="Global Scheme"
                                            value={globalSchemeOptions.find(o => o.value === selectedGlobalScheme) ?? null}
                                            onChange={opt => setSelectedGlobalScheme(opt?.value ?? '')}
                                            options={globalSchemeOptions}
                                            disabled={fetchingGlobal}
                                        />
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={handleFetchGlobalNotes}
                                        disabled={fetchingGlobal || !selectedGlobalScheme}
                                        title="Rebuild this global scheme's contract notes from the current IBKR session"
                                    >
                                        <Zap size={14} className={fetchingGlobal ? styles['spin'] : ''} />
                                        {fetchingGlobal ? 'Fetching…' : 'Fetch Global Notes'}
                                    </Button>
                                </>
                            )}
                            <Button size="sm" onClick={fetchAllocationStatus} disabled={allocLoading}>
                                <RefreshCw size={14} className={allocLoading ? styles['spin'] : ''} />
                                Refresh
                            </Button>
                        </>
                    }
                    padded
                >

                        {/* Custodian status cards grouped by instrument type */}
                        {allocLoading ? (
                            <Placeholder kind="loading" message="Loading allocation status…" />
                        ) : custodians.length === 0 ? (
                            <Placeholder message={<>No custodian allocation data for {dateLabel}.</>} />
                        ) : (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                {Array.from(
                                    custodians.reduce((map, c) => {
                                        const key = c.order_calc_type_display || 'Other';
                                        if (!map.has(key)) map.set(key, []);
                                        map.get(key)!.push(c);
                                        return map;
                                    }, new Map<string, CustodianAllocationStatus[]>())
                                ).map(([groupLabel, groupItems]) => (
                                    <div key={groupLabel} style={{ flex: '0 0 auto' }}>
                                        <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '10px', marginTop: '4px' }}>
                                            <Tag>{groupLabel}</Tag>
                                        </h3>
                                        <div className={styles['custodian-cards']} style={{ marginBottom: 0 }}>
                                            {groupItems.map(custody => {
                                                const ck = cardKey(custody);
                                                const isDone = custody.status === 'done';
                                                return (
                                                    <div key={ck} className={styles['custodian-card']}>
                                                        <div className={styles['custodian-card-header']}>
                                                            <div>
                                                                <div className={styles['custodian-name']}>{custody.name}</div>
                                                                <div className={styles['alloc-count']}>
                                                                    {custody.allocation_count} allocation{custody.allocation_count !== 1 ? 's' : ''}
                                                                </div>
                                                            </div>
                                                            {isDone
                                                                ? <Pill tone="positive">Done</Pill>
                                                                : <Pill tone="warning">Pending</Pill>}
                                                        </div>

                                                        <div className={styles['custodian-actions']}>
                                                            {!isDone && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="primary"
                                                                    onClick={() => handleAllocate(custody)}
                                                                    disabled={!!actionLoading[`allocate-${ck}`]}
                                                                >
                                                                    {actionLoading[`allocate-${ck}`]
                                                                        ? <span className={styles['spinner-sm']} />
                                                                        : <Zap size={13} />
                                                                    }
                                                                    Allocate
                                                                </Button>
                                                            )}

                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => handleDownload(custody)}
                                                                disabled={!!actionLoading[`download-${ck}`] || custody.allocation_count === 0}
                                                            >
                                                                {actionLoading[`download-${ck}`]
                                                                    ? <span className={styles['spinner-sm']} />
                                                                    : <Download size={13} />
                                                                }
                                                                Download
                                                            </Button>

                                                            <Button
                                                                size="sm"
                                                                variant="secondary"
                                                                onClick={() => handleSendEmail(custody)}
                                                                disabled={!!actionLoading[`send-${ck}`] || custody.allocation_count === 0}
                                                            >
                                                                {actionLoading[`send-${ck}`]
                                                                    ? <span className={styles['spinner-sm']} />
                                                                    : <Mail size={13} />
                                                                }
                                                                Send Mail
                                                            </Button>

                                                            {custody.allocation_count > 0 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="danger"
                                                                    onClick={() => handleDeleteAllocation(custody)}
                                                                    disabled={!!actionLoading[`delete-alloc-${ck}`]}
                                                                >
                                                                    {actionLoading[`delete-alloc-${ck}`]
                                                                        ? <span className={styles['spinner-sm']} />
                                                                        : <Trash2 size={13} />
                                                                    }
                                                                    Delete
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Allocation errors — shown outside the cards */}
                        {Object.entries(allocErrors).map(([id, errors]) =>
                            errors.length > 0 && (
                                <div key={id} style={{ marginTop: '12px' }}>
                                    <Note tone="error" onDismiss={() => setAllocErrors(prev => ({ ...prev, [id]: [] }))}>
                                        {errors.map((e, i) => <div key={i}>{e}</div>)}
                                    </Note>
                                </div>
                            )
                        )}

                </Surface>
                )}


            {/* ── Notes Modal ── */}
            <Modal
                open={!!selectedFileNotes}
                title={`Notes: ${selectedFileNotes?.fileName ?? ''}`}
                onClose={() => setSelectedFileNotes(null)}
                maxWidth={1100}
                titleId="contract-file-notes-title"
                closeOnEscape={false}
            >
                {selectedFileNotes && <ContractNotesTable notes={selectedFileNotes.notes} showAllocation />}
            </Modal>
        </PageFrame>
    );
};

export default ContractFilesManage;